import React, { useEffect, useMemo, useState } from 'react';

export default function ResignationForm({ staffId, staffName, role }) {
  const [formData, setFormData] = useState({
    leavingReason: '',
    leavingDate: '',
  });
  const [noticePeriod, setNoticePeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const getPolicies = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/policies', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch company policies.');
        }

        const data = await response.json();
        const currentPolicy = Array.isArray(data?.data) ? data.data[0] : null;
        setNoticePeriod(currentPolicy?.NoticePeriod || '');
      } catch (error) {
        console.error('Failed to load notice period', error);
      }
    };

    getPolicies();
  }, []);

  const leavingDateLabel = useMemo(() => {
    if (!formData.leavingDate) return 'Not selected';
    const parsed = new Date(formData.leavingDate);
    if (Number.isNaN(parsed.getTime())) return formData.leavingDate;
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }, [formData.leavingDate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.insightos.co.uk/api/resignation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            StaffId: staffId,
            StaffName: staffName,
          Role: role,
          LeavingReason: formData.leavingReason,
          LeavingDate: formData.leavingDate,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.message || 'Failed to submit resignation.');
      }

      const result = await response.json();
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to submit resignation.');
      }

      setSuccessMessage(result.message || 'Resignation letter sent. Please check your emails.');
    } catch (error) {
      setErrorMessage(error.message || 'Something went wrong while submitting the resignation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-[28px] border border-white/45 bg-white/60 p-6 shadow-[0_20px_70px_rgba(15,23,42,0.12)] backdrop-blur-2xl sm:p-8">
      <div className="flex flex-col gap-6">
        <div>
          <div className="inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-rose-700">
            Staff resignation
          </div>
          <h3 className="mt-3 text-2xl font-semibold text-slate-900">Resignation Letter Preparation</h3>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            Capture the employee’s leaving information and generate a professional resignation acknowledgement letter.
          </p>
        </div>

        {successMessage && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
            {errorMessage}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/60 bg-white/70 p-4 shadow-sm backdrop-blur-xl">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Staff member</div>
            <div className="mt-2 text-base font-semibold text-slate-900">{staffName}</div>
          </div>
          <div className="rounded-2xl border border-white/60 bg-white/70 p-4 shadow-sm backdrop-blur-xl">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role</div>
            <div className="mt-2 text-base font-semibold text-slate-900">{role || 'Role not available'}</div>
          </div>
          <div className="rounded-2xl border border-white/60 bg-white/70 p-4 shadow-sm backdrop-blur-xl">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notice period</div>
            <div className="mt-2 text-base font-semibold text-slate-900">
              {noticePeriod ? `${noticePeriod} week(s)` : 'Not configured'}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
            <div className="rounded-2xl border border-white/60 bg-white/70 p-5 shadow-sm backdrop-blur-xl">
              <label htmlFor="leavingReason" className="block text-sm font-semibold text-slate-900">
                Leaving Reason
              </label>
              <p className="mt-1 text-sm text-slate-500">
                Record the employee’s reason for leaving so it can be stored in the staff record and referenced in the letter.
              </p>
              <textarea
                id="leavingReason"
                name="leavingReason"
                rows={6}
                required
                value={formData.leavingReason}
                onChange={handleChange}
                placeholder="Example: Personal reasons, relocation, career progression, retirement, etc."
                className="mt-4 block w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
              />
            </div>

            <div className="rounded-2xl border border-white/60 bg-white/70 p-5 shadow-sm backdrop-blur-xl">
              <label htmlFor="leavingDate" className="block text-sm font-semibold text-slate-900">
                Leaving Date
              </label>
              <p className="mt-1 text-sm text-slate-500">
                This date will be added to the staff profile and shown in the resignation acknowledgement letter.
              </p>
              <input
                id="leavingDate"
                name="leavingDate"
                type="date"
                required
                value={formData.leavingDate}
                onChange={handleChange}
                className="mt-4 block w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
              />

              <div className="mt-6 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Letter preview summary</div>
                <dl className="mt-3 space-y-2 text-sm text-slate-700">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-slate-500">Employee</dt>
                    <dd className="font-medium text-slate-900">{staffName}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-slate-500">Role</dt>
                    <dd className="font-medium text-slate-900">{role || '—'}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-slate-500">Notice period</dt>
                    <dd className="font-medium text-slate-900">{noticePeriod ? `${noticePeriod} week(s)` : '—'}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-slate-500">Leaving date</dt>
                    <dd className="font-medium text-slate-900">{leavingDateLabel}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          <button
            type="submit"
            data-component="resignation-form"
            data-feature="submit_staff_resignation_post"
            className={`inline-flex w-full items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition ${
              loading
                ? 'cursor-not-allowed bg-slate-400'
                : 'bg-gradient-to-r from-rose-500 via-pink-500 to-indigo-600 hover:from-rose-600 hover:via-pink-600 hover:to-indigo-700'
            }`}
            disabled={loading}
          >
            {loading ? 'Generating resignation letter…' : 'Generate resignation letter'}
          </button>
        </form>
      </div>
    </div>
  );
}
